import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PipelineStep } from '../types';
import { formatDurationMs } from '../lib/formatDurationMs';

const DOT_COLOR: Record<PipelineStep['status'], string> = {
  completed: 'bg-green-500',
  running: 'bg-blue-500',
  pending: 'bg-muted-foreground/40',
  failed: 'bg-red-500',
};

export function PipelineTimeline({ steps }: { steps: PipelineStep[] }) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  if (steps.length === 0) {
    return <p className="text-sm text-muted-foreground">No pipeline steps.</p>;
  }

  return (
    <div className="space-y-2">
      {/* Timeline row */}
      <div className="flex items-center">
        {steps.map((step, i) => (
          <div key={step.stage} className="flex items-center">
            {i > 0 && (
              <div
                className={cn(
                  'h-px w-6 sm:w-10',
                  step.status === 'pending' ? 'bg-muted-foreground/20' : 'bg-muted-foreground/50',
                )}
              />
            )}
            <button
              type="button"
              className="group flex flex-col items-center"
              onClick={() => setActiveIdx(activeIdx === i ? null : i)}
              title={step.title}
            >
              {step.status === 'running' ? (
                <Loader2 className="size-3.5 animate-spin text-blue-500" />
              ) : (
                <span className={cn('size-3 rounded-full', DOT_COLOR[step.status])} />
              )}
              <span className="mt-1 max-w-[4rem] truncate text-[10px] text-muted-foreground group-hover:text-foreground">
                {step.stage}
              </span>
            </button>
          </div>
        ))}
      </div>

      {/* Detail popover */}
      {activeIdx != null && steps[activeIdx] && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
          <p className="font-medium">{steps[activeIdx].title}</p>
          <p className="text-muted-foreground">{steps[activeIdx].description}</p>
          <p className="mt-1 text-muted-foreground">
            Duration: {formatDurationMs(steps[activeIdx].duration_ms)}
          </p>
        </div>
      )}
    </div>
  );
}
